import React, { useState, useEffect } from 'react';
import { propositionsService } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { CheckCircle, Clock, User, Mail, FileText, AlertCircle, X } from 'lucide-react';

const PropositionsModal = ({ isOpen, onClose, sessionToken, sessionInfo }) => {
  const [propositions, setPropositions] = useState([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen && sessionToken) {
      loadPropositions();
    }
  }, [isOpen, sessionToken]);

  const loadPropositions = async () => {
    try {
      setLoading(true);
      const response = await propositionsService.listPropositions(sessionToken);
      setPropositions(response.propositions || []);
    } catch (error) {
      console.error('Error loading propositions:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les propositions",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!sessionInfo) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <FileText className="w-6 h-6 text-blue-600" />
            Propositions reçues
          </DialogTitle>
          <DialogDescription>
            Collecte pour le comité du {formatDate(sessionInfo.date_comite_cible)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Statistiques */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-blue-900">
                  📊 Résumé de la collecte
                </h3>
                    <div className="flex items-center gap-4 mt-2 text-sm text-blue-800">
                      <span className="flex items-center gap-1">
                        <FileText className="w-4 h-4" />
                        {propositions.length} proposition(s) reçue(s)
                      </span>
                      <span className="flex items-center gap-1">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        {propositions.filter(p => p.statut === 'validé' || p.integre_pv).length} validée(s)
                      </span>
                      <span className="flex items-center gap-1">
                        <X className="w-4 h-4 text-red-600" />
                        {propositions.filter(p => p.statut === 'refusé').length} refusée(s)
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4 text-yellow-600" />
                        {propositions.filter(p => p.statut === 'en_attente').length} en attente
                      </span>
                    </div>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={loadPropositions}
                disabled={loading}
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                ) : (
                  "Actualiser"
                )}
              </Button>
            </div>
          </div>

          {/* Liste des propositions */}
          {loading && propositions.length === 0 ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Chargement des propositions...</p>
            </div>
          ) : propositions.length === 0 ? (
            <div className="text-center py-8">
              <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Aucune proposition reçue
              </h3>
              <p className="text-gray-600">
                Les propositions apparaîtront ici une fois soumises par les membres
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  Propositions détaillées
                </h3>
                <span className="text-sm text-gray-500">
                  {propositions.length} élément(s)
                </span>
              </div>

              <div className="space-y-3">
                {propositions.map((proposition, index) => (
                  <div 
                    key={proposition.id} 
                    className={`border rounded-lg p-4 ${
                      proposition.statut === 'validé' || proposition.integre_pv
                        ? 'bg-green-50 border-green-200' 
                        : proposition.statut === 'refusé'
                        ? 'bg-red-50 border-red-200'
                        : 'bg-white border-gray-200'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded">
                            #{index + 1}
                          </span>
                          
                          {/* Status badges */}
                          {proposition.statut === 'validé' && (
                            <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />
                              {proposition.integre_pv ? 'Intégrée au PV' : 'Validée'}
                            </span>
                          )}
                          {proposition.statut === 'refusé' && (
                            <span className="bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded flex items-center gap-1">
                              <X className="w-3 h-3" />
                              Refusée
                            </span>
                          )}
                          {proposition.statut === 'en_attente' && (
                            <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2 py-1 rounded flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              En attente
                            </span>
                          )}
                        </div>
                        
                        <h4 className="font-medium text-gray-900 text-lg mb-2">
                          {proposition.titre}
                        </h4>
                        
                        {proposition.description && (
                          <p className="text-gray-600 mb-3">
                            {proposition.description}
                          </p>
                        )}
                        
                        {/* Show decision remarks if any */}
                        {proposition.decision_remarques && (
                          <div className="bg-gray-100 p-2 rounded text-sm mb-3">
                            <span className="font-medium text-gray-700">Remarques :</span>
                            <span className="text-gray-600 ml-1">{proposition.decision_remarques}</span>
                          </div>
                        )}
                        
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {proposition.nom_membre}
                          </span>
                          <span className="flex items-center gap-1">
                            <Mail className="w-4 h-4" />
                            {proposition.email_membre}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {formatDateTime(proposition.date_soumission)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end pt-4 border-t">
            <Button onClick={onClose} variant="outline">
              <X className="w-4 h-4 mr-2" />
              Fermer
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PropositionsModal;